// §9.2 Резолюци (Resolution / Propose) tab.
// - Proposable statuses (REQUESTED / DISPUTED) → the inline PROPOSE FORM.
// - Resolved/other statuses → a read-only RESOLUTION SUMMARY.
// The propose submission reuses ReturnApi.propose (the same client + headers as
// the action-bar modal) — no duplicated submit logic; the modal is untouched.
import {
  Badge, Box, Button, Grid, Group, Modal, NumberInput, Paper, Select, SimpleGrid, Skeleton, Slider, Stack, Text, Textarea, TextInput, Title
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { showNotification, i18n } from "@dehub/theme";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { ProposeTemplateApi, ReturnApi } from "../../apis";
import type { IReturnDetail, RefundMethod, ResolutionType, ShippingResponsibility } from "../../apis/return";
import { currencyFormat } from "../../utils/currency";
import { dateFormat, dateTimeSecFormat } from "../../utils/date";
import { DeliveryActionButton } from "../delivery/action-controls";
import { ReplacementFeasibilityWidget } from "./replacement-feasibility-widget";
import { useReturnActions } from "./return-actions";
import classes from "./resolution-panel.module.css";

const TNUM = { fontVariantNumeric: "tabular-nums" } as const;

type TFn = (k: string, o?: Record<string, unknown>) => string;

const resolutionCards = (t: TFn): { value: ResolutionType; label: string; desc: string }[] => [
  { value: "REFUND_MONEY",        label: t("resolutionType.REFUND_MONEY"),        desc: t("resolutionPanel.card.refundDesc") },
  { value: "REPLACEMENT",         label: t("resolutionPanel.card.replacement"),   desc: t("resolutionPanel.card.replacementDesc") },
  { value: "NEXT_ORDER_DISCOUNT", label: t("resolutionType.NEXT_ORDER_DISCOUNT"), desc: t("resolutionPanel.card.discountDesc") },
  { value: "NONE",                label: t("resolutionType.NONE"),                desc: t("resolutionPanel.card.noneDesc") }
];
const refundMethods = (t: TFn): { value: RefundMethod; label: string; eta: string }[] => [
  { value: "BANK_TRANSFER",     label: t("refundMethod.BANK_TRANSFER"),     eta: t("resolutionPanel.eta.bank") },
  { value: "CREDIT_BALANCE",    label: t("refundMethod.CREDIT_BALANCE"),    eta: t("resolutionPanel.eta.instant") },
  { value: "INVOICE_DEDUCTION", label: t("refundMethod.INVOICE_DEDUCTION"), eta: t("resolutionPanel.eta.nextInvoice") },
  { value: "CASH",              label: t("refundMethod.CASH"),              eta: t("resolutionPanel.eta.inPerson") }
];
const shippingOptions = (t: TFn): { value: ShippingResponsibility; label: string }[] => [
  { value: "SELLER_PICKS_UP",      label: t("shipping.responsibility.SELLER_PICKS_UP") },
  { value: "BUYER_SHIPS",          label: t("shipping.responsibility.BUYER_SHIPS") },
  { value: "BUYER_DELIVERS",       label: t("shipping.responsibility.BUYER_DELIVERS") },
  { value: "SELLER_PAYS_SHIPPING", label: t("shipping.responsibility.SELLER_PAYS_SHIPPING") },
  { value: "NOT_REQUIRED",         label: t("shipping.responsibility.NOT_REQUIRED") }
];

const resolutionLabel = (v?: string | null) => (v ? i18n.t(`returns:resolutionType.${v}`) : "-");
const refundMethodLabel = (v?: string | null) => (v ? i18n.t(`returns:refundMethod.${v}`) : "-");
const shippingLabel = (v?: string | null) => (v ? i18n.t(`returns:shipping.responsibility.${v}`) : "-");
const etaOf = (m?: RefundMethod | null) => (m ? i18n.t(`returns:resolutionPanel.etaByMethod.${m}`) : "-");
const fmtAmount = (n: number, _cur?: string) => currencyFormat(n);
const fmtDateTime = (s?: string | null) => dateTimeSecFormat(s);

function notifyOk (msg: string){ showNotification({ color: "green", title: i18n.t("returns:toast.success"), message: msg }); }
function notifyErr (e: unknown){ showNotification({ color: "red", title: i18n.t("returns:toast.error"), message: (e as Error).message }); }

// Selectable card driven by a data-attribute (no class switching).
function ChoiceCard ({ active, onClick, title, subtitle, badge }: {
  active: boolean; onClick: () => void; title: string; subtitle?: string; badge?: React.ReactNode;
}){
  return (
    <Box className={classes.choice} data-active={active || undefined} onClick={onClick} role="button" tabIndex={0}>
      <Group justify="space-between" gap="xs" wrap="nowrap">
        <Text size="sm" fw={600}>{title}</Text>
        {badge}
      </Group>
      {subtitle ? <Text size="sm" c="dimmed">{subtitle}</Text> : null}
    </Box>
  );
}

interface Props {
  detail: IReturnDetail;
  onChange: () => void;
}

export function ReturnResolutionPanel ({ detail, onChange }: Props){
  const canPropose = detail.status === "REQUESTED" || detail.status === "DISPUTED";
  return canPropose
    ? <ProposeForm detail={detail} onChange={onChange} />
    : <ResolutionSummary detail={detail} />;
}

// ── Read-only summary (resolved / non-proposable statuses) ──
function ResolutionSummary ({ detail }: { detail: IReturnDetail }){
  const { t } = useTranslation("returns");
  const cur = detail.currency;
  const refundAmount = detail.refundAmount ?? null;
  return (
    <Stack gap="md">
      <Paper withBorder radius="md" p="md">
        <Title order={6} mb="xs">{t("detail.tab.resolution")}</Title>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <FieldRow label={t("resolutionPanel.type")} value={detail.resolutionType ? resolutionLabel(detail.resolutionType) : "-"} />
          <FieldRow label={t("resolutionPanel.refundPercent")} value={detail.refundPercent != null ? `${detail.refundPercent}%` : "-"} />
          <FieldRow label={t("resolutionPanel.refundAmount")} value={refundAmount != null ? fmtAmount(refundAmount, cur) : "-"} />
          <FieldRow label={t("resolutionPanel.refundChannel")} value={detail.refundMethod ? refundMethodLabel(detail.refundMethod) : "-"} />
          <FieldRow label={t("resolutionPanel.shippingResponsibility")} value={detail.shippingResponsibility ? shippingLabel(detail.shippingResponsibility) : "-"} />
          <FieldRow label={t("resolutionPanel.targetWarehouse")} value={detail.targetWarehouseName || "-"} />
        </SimpleGrid>
        {detail.proposalNotes ? (
          <Stack gap={4} mt="sm">
            <Text size="sm" c="dimmed">{t("resolutionPanel.proposalNotesLabel")}</Text>
            <Paper withBorder radius="sm" p="sm" bg="gray.0"><Text size="sm">{detail.proposalNotes}</Text></Paper>
          </Stack>
        ) : null}
      </Paper>

      <Paper withBorder radius="md" p="md">
        <Title order={6} mb="xs">{t("resolutionPanel.decision")}</Title>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          <FieldRow label={t("resolutionPanel.proposedBy")} value={detail.proposedByName || "-"} />
          <FieldRow label={t("resolutionPanel.proposedAt")} value={fmtDateTime(detail.proposedAt)} />
          <FieldRow label={t("resolutionPanel.acceptedBy")} value={detail.acceptedByName || "-"} />
          <FieldRow label={t("resolutionPanel.acceptedAt")} value={fmtDateTime(detail.acceptedAt)} />
        </SimpleGrid>
      </Paper>
    </Stack>
  );
}

function FieldRow ({ label, value }: { label: string; value: React.ReactNode }){
  return (
    <Group justify="space-between" gap="xs" wrap="nowrap">
      <Text size="sm" c="dimmed">{label}</Text>
      <Text size="sm" fw={500} style={TNUM}>{value}</Text>
    </Group>
  );
}

// ── Live refund ETA preview (D1) — drives the wording from the backend compute ──
// endpoint instead of hardcoded per-channel copy. Falls back to the static hint
// on error / while no channel is chosen.
function RefundEtaPreview ({ requestType, refundMethod, enabled }: {
  requestType: string; refundMethod: RefundMethod | null; enabled: boolean;
}){
  const { t } = useTranslation("returns");
  const { data, isLoading, error } = useSWR(
    enabled && refundMethod ? ["returns.refund-eta", requestType, refundMethod] : null,
    () => ReturnApi.previewRefundTimeline({ requestType, refundMethod: refundMethod as string }),
    { revalidateOnFocus: false }
  );
  const preview = data?.previews?.find((p) => p.refundMethod === refundMethod) ?? data?.previews?.[0] ?? null;

  return (
    <Paper withBorder radius="md" p="md">
      <Title order={6} mb="xs">{t("resolutionPanel.refundEta")}</Title>
      {!enabled ? (
        <Text size="sm" c="dimmed">—</Text>
      ) : !refundMethod ? (
        <Text size="sm" c="dimmed">{t("resolutionPanel.selectChannel")}</Text>
      ) : isLoading ? (
        <Skeleton h={44} />
      ) : error || !preview ? (
        <Text size="sm">{etaOf(refundMethod)}</Text>
      ) : (
        <Stack gap={4}>
          <Text size="sm" fw={600} style={TNUM}>
            {preview.estimatedRefundDays <= 0 ? t("resolutionPanel.eta.instant") : t("resolutionPanel.businessDays", { count: preview.estimatedRefundDays })}
          </Text>
          {preview.estimatedCompletionAt ? (
            <Text size="sm" c="dimmed" style={TNUM}>
              {t("resolutionPanel.completionDate")}: {dateFormat(preview.estimatedCompletionAt)}
            </Text>
          ) : null}
          {preview.basedOn ? <Text size="sm" c="dimmed">{t("resolutionPanel.basedOn")}: {preview.basedOn}</Text> : null}
        </Stack>
      )}
    </Paper>
  );
}

// ── Propose form (REQUESTED / DISPUTED) ──
function ProposeForm ({ detail, onChange }: Props){
  const { t } = useTranslation("returns");
  const { stateOf } = useReturnActions();
  const proposeState = stateOf("propose", detail);
  const RESOLUTION_CARDS = resolutionCards(t);
  const REFUND_METHODS = refundMethods(t);
  const SHIPPING_OPTIONS = shippingOptions(t);
  const cur = detail.currency;
  const total = detail.totalRequestedAmount ?? 0;
  const pref = detail.preferredRefundMethod ?? null;
  const prefUsable = pref && !(pref === "INVOICE_DEDUCTION" && !detail.invoiceId);

  const [resolution, setResolution] = useState<ResolutionType>("REFUND_MONEY");
  const [refundPercent, setRefundPercent] = useState(100);
  const [deductionReason, setDeductionReason] = useState("");
  const [refundMethod, setRefundMethod] = useState<RefundMethod | null>(prefUsable ? pref : "BANK_TRANSFER");
  const [shipping, setShipping] = useState<ShippingResponsibility>(detail.shippingResponsibility ?? "SELLER_PICKS_UP");
  const [proposalNotes, setProposalNotes] = useState("");
  // §T2a/§T2b — pickup SLA + target-warehouse override (editable).
  const [pickupSlaDays, setPickupSlaDays] = useState<number>(detail.pickupSlaDays ?? 14);
  const [targetWarehouseId, setTargetWarehouseId] = useState<string | null>(detail.targetWarehouseId);
  const [warehouseReason, setWarehouseReason] = useState("");
  const [busy, setBusy] = useState(false);

  const [saveOpen, saveH] = useDisclosure(false);
  const [tmplName, setTmplName] = useState("");

  const { data: tmplData, mutate: tmplMutate } = useSWR(
    "propose-templates.list", () => ProposeTemplateApi.list(), { revalidateOnFocus: false }
  );
  const templates = tmplData?.items ?? [];

  // §T2b — the seller's warehouses for the target picker.
  const { data: whData } = useSWR("returns.warehouses", () => ReturnApi.listWarehouses(), { revalidateOnFocus: false });
  const warehouseOptions = (whData?.warehouses ?? []).map((w) => ({
    value: w.id,
    label: w.code ? `${w.name} · ${w.code}` : w.name
  }));
  // Keep the current target selectable even before the list loads / if it is
  // not among the seller's own warehouses.
  if (detail.targetWarehouseId && !warehouseOptions.some((o) => o.value === detail.targetWarehouseId)) {
    warehouseOptions.unshift({
      value: detail.targetWarehouseId,
      label: detail.targetWarehouseName ?? "-"
    });
  }

  const refundAmount = Math.round((total * refundPercent) / 100);
  const isMoney = resolution === "REFUND_MONEY";
  const overriding = isMoney && refundMethod != null && pref != null && refundMethod !== pref && detail.buyerOverrideAllowed;
  const needsDeduction = refundPercent < 100;
  const needsOverrideNote = overriding && !proposalNotes.trim();
  // §T2b (R-213) — replacing a target warehouse requires an override reason.
  // README §7.6.13 — setting the first one (create left it empty) does not.
  const warehouseChanged = (targetWarehouseId ?? null) !== (detail.targetWarehouseId ?? null);
  const replacesWarehouse = warehouseChanged && !!detail.targetWarehouseId;
  const needsWarehouseReason = replacesWarehouse && !warehouseReason.trim();
  // Spec §6 validation: NOT_REQUIRED is incompatible with damaged-delivery /
  // wrong-item reasons (the goods must physically come back). Backend rejects
  // this too; we guard client-side so the bad submit is caught before the call.
  const shippingConflict =
    shipping === "NOT_REQUIRED" &&
    (detail.returnReason === "DELIVERY_DAMAGED" || detail.returnReason === "WRONG_ITEM_DELIVERED");
  const valid =
    !!shipping &&
    !!resolution &&
    (!isMoney || !!refundMethod) &&
    (!needsDeduction || !!deductionReason.trim()) &&
    !needsOverrideNote &&
    !needsWarehouseReason &&
    !shippingConflict;

  const applyTemplate = (id: string) => {
    const tpl = templates.find((x) => x.id === id);
    if (!tpl) return;
    setResolution(tpl.resolutionType as ResolutionType);
    setRefundPercent(tpl.refundPercent);
    setDeductionReason(tpl.deductionReason ?? "");
    setShipping(tpl.shippingResponsibility as ShippingResponsibility);
    setRefundMethod((tpl.refundMethod as RefundMethod) ?? null);
    setProposalNotes(tpl.proposalNotesTemplate ?? "");
    notifyOk(t("resolutionPanel.toast.templateApplied", { name: tpl.name }));
  };

  const submit = async () => {
    setBusy(true);
    try {
      const body: Parameters<typeof ReturnApi.propose>[1] = {
        refundPercent,
        // O05 — required below 100% (see `valid`); never a placeholder.
        deductionReason: needsDeduction ? deductionReason.trim() : null,
        shippingResponsibility: shipping,
        proposalNotes: proposalNotes.trim() || null,
        resolutionType: resolution,
        refundMethod: isMoney ? refundMethod : null,
        expectedVersion: detail.version
      };
      // §T2a — only send pickupSlaDays when the keeper changed it.
      if (pickupSlaDays !== (detail.pickupSlaDays ?? 14)) body.pickupSlaDays = pickupSlaDays;
      // §T2b — only send the target warehouse + reason on a real change.
      if (warehouseChanged) {
        body.targetWarehouseId = targetWarehouseId;
        if (replacesWarehouse) body.targetWarehouseChangeReason = warehouseReason.trim();
      }
      await ReturnApi.propose(detail.id, body);
      notifyOk(t("resolutionPanel.toast.proposalSent"));
      onChange();
    } catch (e) { notifyErr(e); }
    finally { setBusy(false); }
  };

  const saveTemplate = async () => {
    if (!tmplName.trim()) return;
    setBusy(true);
    try {
      await ProposeTemplateApi.create({
        name: tmplName.trim(),
        refundPercent,
        deductionReason: deductionReason.trim() || null,
        shippingResponsibility: shipping,
        resolutionType: resolution,
        refundMethod: isMoney ? refundMethod : null,
        proposalNotesTemplate: proposalNotes.trim() || null
      });
      notifyOk(t("resolutionPanel.toast.templateSaved"));
      saveH.close(); setTmplName(""); tmplMutate();
    } catch (e) { notifyErr(e); }
    finally { setBusy(false); }
  };

  return (
    <>
    <Grid>
      {/* Form (2/3) */}
      <Grid.Col span={{ base: 12, md: 8 }}>
        <Stack gap="md">
          {/* 1. Resolution type cards */}
          <Paper withBorder radius="md" p="md">
            <Title order={6} mb="xs">{t("resolutionPanel.resolutionTypeTitle")}</Title>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
              {RESOLUTION_CARDS.map((r) => (
                <ChoiceCard key={r.value} active={resolution === r.value} onClick={() => setResolution(r.value)} title={r.label} subtitle={r.desc} />
              ))}
            </SimpleGrid>
          </Paper>

          {/* 2. Refund percent slider + live amount */}
          <Paper withBorder radius="md" p="md">
            <Group justify="space-between" mb="xs">
              <Title order={6}>{t("resolutionPanel.refundPercentTitle")}</Title>
              <Text size="sm" fw={600} style={TNUM}>{refundPercent}%</Text>
            </Group>
            <Slider value={refundPercent} onChange={setRefundPercent} min={0} max={100} step={5}
              marks={[{ value: 0 }, { value: 50 }, { value: 100 }]} />
            <Text size="sm" mt="sm">
              {t("resolutionPanel.refundAmountLabel")}:{" "}
              <Text span fw={600} style={TNUM}>{fmtAmount(refundAmount, cur)}</Text>
              <Text span size="sm" c="dimmed"> ({currencyFormat(total)} × {refundPercent}%)</Text>
            </Text>
            {needsDeduction ? (
              <Textarea mt="sm" label={t("resolutionPanel.deductionReasonLabel")} required
                value={deductionReason} onChange={(e) => setDeductionReason(e.currentTarget.value)}
                error={needsDeduction && !deductionReason.trim() ? t("resolutionPanel.required") : undefined} />
            ) : null}
          </Paper>

          {/* 3. Refund channel (only for REFUND_MONEY) */}
          {isMoney ? (
            <Paper withBorder radius="md" p="md">
              <Title order={6} mb="xs">{t("resolutionPanel.refundChannel")}</Title>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                {REFUND_METHODS.map((m) => {
                  const blocked = m.value === "INVOICE_DEDUCTION" && !detail.invoiceId;
                  return (
                    <ChoiceCard key={m.value} active={refundMethod === m.value}
                      onClick={() => { if (!blocked) setRefundMethod(m.value); }}
                      title={m.label} subtitle={blocked ? t("resolutionPanel.noInvoiceBlocked") : m.eta}
                      badge={pref === m.value ? <Badge size="lg" color="gray" variant="light" radius="xl">{t("resolutionPanel.preferredBadge")}</Badge> : undefined} />
                  );
                })}
              </SimpleGrid>
              {overriding ? (
                <Text size="sm" c="var(--nw-text-2)" mt="xs">
                  {t("resolutionPanel.overrideChannelHint")}
                </Text>
              ) : null}
            </Paper>
          ) : null}

          {/* 4. Shipping responsibility (D11) */}
          <Paper withBorder radius="md" p="md">
            <Title order={6} mb="xs">{t("resolutionPanel.shippingTitle")}</Title>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
              {SHIPPING_OPTIONS.map((s) => (
                <ChoiceCard key={s.value} active={shipping === s.value} onClick={() => setShipping(s.value)} title={s.label} />
              ))}
            </SimpleGrid>
            {shippingConflict ? (
              <Text size="sm" c="var(--mantine-color-error)" mt="xs">
                {t("resolutionPanel.shippingConflict")}
              </Text>
            ) : null}
          </Paper>

          {/* 5. Target warehouse (D12) — editable override (R-213) */}
          <Paper withBorder radius="md" p="md">
            <Title order={6} mb="xs">{t("resolutionPanel.targetWarehouseTitle")}</Title>
            <Select
              label={t("resolutionPanel.warehouse")}
              placeholder={warehouseOptions.length ? t("resolutionPanel.warehousePlaceholder") : t("resolutionPanel.warehouseLoading")}
              data={warehouseOptions}
              value={targetWarehouseId}
              onChange={setTargetWarehouseId}
              searchable
              nothingFoundMessage={t("resolutionPanel.warehouseNotFound")}
            />
            {replacesWarehouse ? (
              <Textarea
                mt="sm"
                label={t("resolutionPanel.warehouseChangeReasonLabel")}
                required
                value={warehouseReason}
                onChange={(e) => setWarehouseReason(e.currentTarget.value)}
                error={needsWarehouseReason ? t("resolutionPanel.required") : undefined}
                autosize
                minRows={2}
              />
            ) : (
              <Text size="sm" c="dimmed" mt="xs">{t("resolutionPanel.warehouseDefaultHint")}</Text>
            )}
          </Paper>

          {/* 6. Pickup SLA (D11) — editable */}
          <Paper withBorder radius="md" p="md">
            <Title order={6} mb="xs">{t("resolutionPanel.pickupSlaTitle")}</Title>
            <NumberInput
              label={t("resolutionPanel.days")}
              value={pickupSlaDays}
              onChange={(v) => setPickupSlaDays(typeof v === "number" ? v : 14)}
              min={1}
              max={90}
              w={160}
            />
            <Text size="sm" c="dimmed" mt="xs">{t("resolutionPanel.pickupSlaHint", { count: detail.pickupSlaDays ?? 14 })}</Text>
          </Paper>

          {/* 7. Proposal notes */}
          <Paper withBorder radius="md" p="md">
            <Title order={6} mb="xs">{t("resolutionPanel.notesTitle")}</Title>
            <Textarea value={proposalNotes} onChange={(e) => setProposalNotes(e.currentTarget.value)} autosize minRows={3}
              error={needsOverrideNote ? t("resolutionPanel.overrideNoteRequired") : undefined} />
          </Paper>

          <Group justify="flex-end">
            <Button variant="default" onClick={saveH.open}>{t("resolutionPanel.saveTemplate")}</Button>
            <DeliveryActionButton
              state={{ allowed: proposeState.enabled, reason: proposeState.reason }}
              disabledReason={!valid ? t("actionsDialog.fixFields") : undefined}
              onClick={submit}
              loading={busy}
            >
              {t("resolutionPanel.sendProposal")}
            </DeliveryActionButton>
          </Group>
        </Stack>
      </Grid.Col>

      {/* Helpers (1/3) */}
      <Grid.Col span={{ base: 12, md: 4 }}>
       <Stack gap="md">
        <Paper withBorder radius="md" p="md">
          <Title order={6} mb="xs">{t("resolutionPanel.templates")}</Title>
          {templates.length === 0 ? (
            <Text size="sm" c="dimmed">{t("resolutionPanel.noTemplates")}</Text>
          ) : (
            <Stack gap="xs">
              {templates.map((t) => (
                <Button key={t.id} size="sm" variant="light" onClick={() => applyTemplate(t.id)}>{t.name}</Button>
              ))}
            </Stack>
          )}
        </Paper>

        <RefundEtaPreview requestType={detail.requestType} refundMethod={refundMethod} enabled={isMoney} />

        <ReplacementFeasibilityWidget
          returnRequestId={detail.id}
          enabled={resolution === "REPLACEMENT" || resolution === "MIXED"}
        />

        {pref ? (
          <Paper withBorder radius="md" p="md">
            <Title order={6} mb="xs">{t("resolutionPanel.buyerPreference")}</Title>
            <Group gap="xs">
              <Text size="sm">{refundMethodLabel(pref)}</Text>
              <Badge size="lg" color="gray" variant="light" radius="xl">{t("resolutionPanel.preferredBadge")}</Badge>
            </Group>
            <Text size="sm" c="dimmed" mt="xs">
              {detail.buyerOverrideAllowed ? t("resolutionPanel.overrideAllowedWithNote") : t("resolutionPanel.overrideForbidden")}
            </Text>
          </Paper>
        ) : null}
       </Stack>
      </Grid.Col>
    </Grid>

    {/* Save template dialog */}
    <Modal opened={saveOpen} onClose={saveH.close} title={t("resolutionPanel.saveTemplate")} size="sm">
      <Stack gap="md">
        <TextInput label={t("resolutionPanel.templateName")} required value={tmplName} onChange={(e) => setTmplName(e.currentTarget.value)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={saveH.close}>{t("resolutionPanel.cancel")}</Button>
          <Button onClick={saveTemplate} loading={busy} disabled={!tmplName.trim()}>{t("resolutionPanel.save")}</Button>
        </Group>
      </Stack>
    </Modal>
    </>
  );
}
